import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import * as moment from 'moment/moment';
import translate from '../../commonComponents/translate';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import Title from '../../commonComponents/Title';
import SubscriptionProp from './propTypes/subscriptionProp';
import SubscriptionOrderHeader from './components/SubscriptionOrderHeader';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import UserProp from '../../commonComponents/propTypes/userProp';
import FormInput from '../../commonComponents/forms/FormInput';
import DateRange from '../../commonComponents/forms/inputs/DateRange';
import {
  findCurrencySymbol,
  formatPrice,
  SUBSCRIPTION_STATUSES,
} from '../../services/utilityFunctions';
import CurrencyProp from '../profile/propTypes/currencyProp';
import StripeCardProp from '../profile/propTypes/StripeCardProp';
import TenantProp from '../../commonComponents/propTypes/tenantProp';
import ServerSideList from '../../commonComponents/lists/ServerSideList';

function ShopUserSubscriptionsOrderPage({
  t,
  subscription,
  countries,
  user,
  currencies,
  cards: paramCards,
  _csrf,
  tenant,
}) {
  const [query, setQuery] = useState({ status: '' });
  const [filters, setFilters] = useState({ startDate: null, endDate: null });

  const handleDateChange = (startDate, endDate) => {
    setFilters(prevState => ({ ...prevState, startDate, endDate }));
  };

  const statusText = status => t(`shop.${status}`);

  const updateQueryStatus = value => {
    setQuery(prev => ({
      ...prev,
      status: value === '0' ? '' : value,
    }));
  };

  let statusItems = [];
  statusItems = [
    { text: t('all'), id: '0' },
    ...SUBSCRIPTION_STATUSES.map(s => ({
      text: statusText(s.status),
      id: s.status,
    })),
  ];

  const dataTableUrl = '/table/market/order/list';

  const columns = [
    {
      field: 'date',
      textAlign: 'left',
      title: t('shop.date'),
      width: 100,
      sortable: false,
      template: item =>
        renderToStaticMarkup(
          <>{moment(item.createdAt).format('DD MMM YYYY')}</>,
        ),
    },
    {
      field: 'invoice.number',
      textAlign: 'left',
      title: t('order.subscription.invoiceNumber'),
      width: 130,
      sortable: false,
      template: item =>
        renderToStaticMarkup(
          <span className={`order-status ${item.status}`}>
            {item?.invoice?.number || 'N/A'}
          </span>,
        ),
    },
    {
      field: 'status',
      textAlign: 'left',
      title: t('shop.status'),
      width: 100,
      sortable: false,
      template: item =>
        renderToStaticMarkup(
          <span className={`order-status ${item.status}`}>
            {statusText(item.status)}
          </span>,
        ),
    },
    {
      field: 'price',
      textAlign: 'left',
      title: t('shop.price'),
      width: 80,
      sortable: false,
      template: ({ total, currency }) =>
        renderToStaticMarkup(
          <>
            {formatPrice(
              total,
              findCurrencySymbol(currency, currencies) || '€',
            )}
          </>,
        ),
    },
    {
      field: 'invoice',
      textAlign: 'left',
      title: t('shop.invoice'),
      width: 120,
      sortable: false,
      template: order =>
        renderToStaticMarkup(
          order?.invoice?.number ? (
            <a
              href={`/shop/invoice/${order._id}?type=market`}
              target="_blank"
              rel="noreferrer"
            >
              {order.invoice.number}
            </a>
          ) : (
            <>N/A</>
          ),
        ),
    },
  ];
  return (
    <>
      <Title title={t('order.my_subscription')}>
        <ContentContainer>
          <SubHeader title={t('order.my_subscription')} />
          <div id="userOrderPage" className="m-content shop">
            <div className="m-portlet__body userOrderPage-container">
              <div className="row col-lg-12 userOrderPage-wrapper">
                <div className="col-lg-12 userOrderPage-wrapper">
                  <div className="m-portlet">
                    <div className="m-portlet__body m-portlet__body--no-padding">
                      <div className="m-invoice-2">
                        <div className="m-invoice__wrapper">
                          <SubscriptionOrderHeader
                            countries={countries}
                            user={user}
                            subscription={subscription}
                            t={t}
                            paramCards={paramCards}
                            currencies={currencies}
                            _csrf={_csrf}
                            tenant={tenant}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="m-portlet__body userOrderPage-container">
              <div className="row col-lg-12 userOrderPage-wrapper">
                <div className="col-lg-12 userOrderPage-wrapper">
                  <div className="m-portlet">
                    <div className="m-portlet__body m-portlet__body--no-padding">
                      <div className="m-invoice-2">
                        <div className="m-invoice__wrapper">
                          <div className="subscriptions-list-wrapper">
                            <div className="row col-lg-12 col-12">
                              <h5 className="orders-list col-12">
                                {t('order.subscription.ordersList')}
                              </h5>
                              <FormInput
                                type="select"
                                onChange={updateQueryStatus}
                                id="status-order"
                                placeholder={t('shop.status')}
                                value=""
                                groupProps={{
                                  className: 'col-lg-3',
                                }}
                                inputProps={{
                                  items: statusItems,
                                  allowClear: true,
                                }}
                              />
                              <div className="col-lg-3">
                                <DateRange
                                  placeholder={t('shop.date')}
                                  startDate={filters.startDate}
                                  endDate={filters.endDate}
                                  onChange={({ startDate, endDate }) => {
                                    handleDateChange(startDate, endDate);
                                  }}
                                  onClear={() => {
                                    handleDateChange(null, null);
                                  }}
                                />
                              </div>
                            </div>
                            <ServerSideList
                              columns={columns}
                              id="orders-subscription-list"
                              dataSourceURL={dataTableUrl}
                              query={{
                                ...filters,
                                ...query,
                                orderSubscription: subscription._id,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

ShopUserSubscriptionsOrderPage.propTypes = {
  t: PropTypes.func.isRequired,
  subscription: PropTypes.shape(SubscriptionProp.propType).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  user: PropTypes.shape(UserProp.propType).isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)).isRequired,
  _csrf: PropTypes.string.isRequired,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
};

export default translate(ShopUserSubscriptionsOrderPage);
